import { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AIModeProvider } from "@/contexts/AIModeContext";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useIdleTimeout } from "@/hooks/use-idle-timeout";
import { useUsageAlerts } from "@/hooks/use-usage-alerts";
import { PUBLIC_ROUTES, SHELL_ROUTES, SLAW_ROUTES, CATCH_ALL_ROUTE } from "@/lib/route-config";
import { isSLAWDomain } from "@/lib/domain-detection";
import SLAWShell from "@/components/slaw/SLAWShell";
import CookieConsentBanner from "@/components/CookieConsentBanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        if (error?.status >= 400 && error?.status < 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    },
  },
});

function PageLoader() {
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Loading page…</p>
    </div>
  );
}

function SuspenseRoute({ children }: { children: React.ReactNode }) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </RouteErrorBoundary>
  );
}

function ProtectedSuspenseRoute({ children, requireAdmin }: { children: React.ReactNode; requireAdmin?: boolean }) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <ProtectedRoute requireAdmin={requireAdmin}>
          {children}
        </ProtectedRoute>
      </Suspense>
    </RouteErrorBoundary>
  );
}

function IdleTimeoutGuard() {
  useIdleTimeout();
  return null;
}

function UsageAlertGuard() {
  useUsageAlerts();
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AIModeProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <IdleTimeoutGuard />
          <UsageAlertGuard />
          <UpgradePrompt />
          <CookieConsentBanner />
          <Routes>
            {/* Domain-based redirect: slawscore.org root → SLAW landing page */}
            {isSLAWDomain() && (
              <Route path="/" element={<Navigate to="/slaw-landing" replace />} />
            )}

            {/* Public routes (no shell) */}
            {PUBLIC_ROUTES.map(r => (
              <Route key={r.path} path={r.path} element={<SuspenseRoute><r.component /></SuspenseRoute>} />
            ))}

            {/* SLAW routes (SLAWShell layout) */}
            <Route element={<SLAWShell />}>
              {SLAW_ROUTES.map(r => (
                <Route key={r.path} path={r.path} element={<SuspenseRoute><r.component /></SuspenseRoute>} />
              ))}
            </Route>

            {/* Shell routes */}
            <Route element={<AppShell />}>
              {SHELL_ROUTES.map(r => {
                const El = r.component;
                return (
                  <Route
                    key={r.path}
                    path={r.path}
                    element={
                      r.protected ? (
                        <ProtectedSuspenseRoute requireAdmin={r.requireAdmin}><El /></ProtectedSuspenseRoute>
                      ) : (
                        <SuspenseRoute><El /></SuspenseRoute>
                      )
                    }
                  />
                );
              })}
            </Route>

            {/* Catch-all */}
            <Route path={CATCH_ALL_ROUTE.path} element={<SuspenseRoute><CATCH_ALL_ROUTE.component /></SuspenseRoute>} />
          </Routes>
        </BrowserRouter>
      </AIModeProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
